import { gql, useQuery } from '@apollo/client';
import {
  BuildingIcon,
  ChartBarHorizontalIcon,
  ChartBarIcon,
  ChartLineUpIcon,
  ProjectorScreenChartIcon,
} from '@phosphor-icons/react';
import { FC } from 'react';
import { Outlet, useOutletContext, useParams } from 'react-router-dom';

import SidebarLayout, {
  SidebarLayoutContent,
  SidebarLayoutNavigation,
} from 'layouts/SidebarLayout';

import ErrorMessage from 'components/ErrorMessage';

import LoadingIndicator from 'primitives/LoadingIndicator';

import analyticsIcon from 'media/icons/coins-document-chart.svg';
import investmentIcon from 'media/icons/investment.svg';
import networkIcon from 'media/icons/network.svg';
import portfolioIcon from 'media/icons/portfolio.svg';

const SYNDICATE_ANALYTICS_QUERY = gql`
  query SyndicateAnalyticsPage($id: ID!) {
    syndicate(id: $id) {
      id
      name
      analytics {
        overview {
          totalInvestedAmount
          totalRealizedValue
          totalUnrealizedValue
          numberOfDeals
          numberOfCompanies
          dpi
          xirr
          tvpi
          rvpi
          markupRate
          lossRatio
        }
        schemeMetrics {
          schemeName
          investedCapital
          realizedValue
          unrealizedValue
          totalValue
          numberOfDeals
          numberOfCompanies
          dpi
          xirr
          tvpi
          rvpi
          markupRate
          lossRatio
          contributionToOverallDpi
        }
        dpiBreakdown {
          schemeName
          investedCapital
          realizedValue
          unrealizedValue
          totalValue
          numberOfDeals
          numberOfCompanies
          dpi
          xirr
          tvpi
          rvpi
          markupRate
          lossRatio
          contributionToOverallDpi
        }
        companyMetrics {
          companyId
          companyName
          companyImage
          schemeNames
          investmentTimeline {
            dealId
            schemeName
            investmentDate
            investmentYear
            investedCapital
          }
          investedCapital
          realizedValue
          unrealizedValue
          totalValue
          dpi
          tvpi
          rvpi
          markupPercentage
          yearWiseValuations {
            year
            investedCapital
            valuation
            markupPercentage
            calculations {
              dealId
              schemeName
              investmentDate
              investmentYear
              investedCapital
              valuationEffectiveOn
              valuationMultiple
              valuation
              markupPercentage
            }
          }
        }
        yearWisePerformance {
          year
          numberOfDeals
          numberOfCompanies
          investedCapital
          realizedValue
          unrealizedValue
          dpi
          xirr
          tvpi
          rvpi
          stageBreakdown {
            stage
            percentage
          }
          sectorBreakdown {
            industry
            percentage
          }
          companies {
            companyId
            companyName
            companyImage
          }
        }
      }
    }
  }
`;

export type SyndicateAnalyticsData = {
  overview: {
    totalInvestedAmount: number;
    totalRealizedValue: number;
    totalUnrealizedValue: number;
    numberOfDeals: number;
    numberOfCompanies: number;
    dpi: number;
    xirr?: number | null;
    tvpi: number;
    rvpi: number;
    markupRate: number;
    lossRatio: number;
  };
  schemeMetrics: Array<{
    schemeName: string;
    investedCapital: number;
    realizedValue: number;
    unrealizedValue: number;
    totalValue: number;
    numberOfDeals: number;
    numberOfCompanies: number;
    dpi: number;
    xirr?: number | null;
    tvpi: number;
    rvpi: number;
    markupRate: number;
    lossRatio: number;
    contributionToOverallDpi: number;
  }>;
  dpiBreakdown: Array<{
    schemeName: string;
    investedCapital: number;
    realizedValue: number;
    unrealizedValue: number;
    totalValue: number;
    numberOfDeals: number;
    numberOfCompanies: number;
    dpi: number;
    xirr?: number | null;
    tvpi: number;
    rvpi: number;
    markupRate: number;
    lossRatio: number;
    contributionToOverallDpi: number;
  }>;
  companyMetrics: Array<{
    companyId: string;
    companyName: string;
    companyImage?: string | null;
    schemeNames: string[];
    investmentTimeline: Array<{
      dealId: string;
      schemeName: string;
      investmentDate: string;
      investmentYear: number;
      investedCapital: number;
    }>;
    investedCapital: number;
    realizedValue: number;
    unrealizedValue: number;
    totalValue: number;
    dpi: number;
    tvpi: number;
    rvpi: number;
    markupPercentage: number;
    yearWiseValuations: Array<{
      year: number;
      investedCapital: number;
      valuation: number;
      markupPercentage: number;
      calculations: Array<{
        dealId: string;
        schemeName: string;
        investmentDate: string;
        investmentYear: number;
        investedCapital: number;
        valuationEffectiveOn: string;
        valuationMultiple: number;
        valuation: number;
        markupPercentage: number;
      }>;
    }>;
  }>;
  yearWisePerformance: Array<{
    year: number;
    numberOfDeals: number;
    numberOfCompanies: number;
    investedCapital: number;
    realizedValue: number;
    unrealizedValue: number;
    dpi: number;
    xirr?: number | null;
    tvpi: number;
    rvpi: number;
    stageBreakdown: Array<{
      stage: number;
      percentage: number;
    }>;
    sectorBreakdown: Array<{
      industry: number;
      percentage: number;
    }>;
    companies: Array<{
      companyId: string;
      companyName: string;
      companyImage?: string | null;
    }>;
  }>;
};

type AnalyticsContextType = {
  analytics: SyndicateAnalyticsData;
  syndicateId: string;
};

const SyndicateAnalytics: FC = () => {
  const { syndicateId } = useParams<{ syndicateId: string }>() as { syndicateId: string };

  const { data, loading, error, refetch } = useQuery(SYNDICATE_ANALYTICS_QUERY, {
    variables: {
      id: syndicateId,
    },
  });

  const tabs = [
    {
      key: 'overview',
      label: 'Overview',
      description: 'Top-level syndicate analytics',
      route: `/syndicate-dashboard/${syndicateId}/analytics`,
      icon: ChartBarIcon,
      exact: true,
    },
    {
      key: 'companies',
      label: 'Companies',
      description: 'Aggregated company analytics',
      route: `/syndicate-dashboard/${syndicateId}/analytics/companies`,
      icon: BuildingIcon,
    },
    {
      key: 'scheme-metrics',
      label: 'Scheme Metrics',
      description: 'Scheme-wise performance metrics',
      route: `/syndicate-dashboard/${syndicateId}/analytics/scheme-metrics`,
      icon: ChartLineUpIcon,
    },
    {
      key: 'year-wise-performance',
      label: 'Year-wise Performance',
      description: 'Calendar year investment performance',
      route: `/syndicate-dashboard/${syndicateId}/analytics/year-wise-performance`,
      icon: ChartBarHorizontalIcon,
    },
    {
      key: 'dpi-breakdown',
      label: 'DPI Breakdown',
      description: 'Scheme-level DPI contributions',
      route: `/syndicate-dashboard/${syndicateId}/analytics/dpi-breakdown`,
      icon: ProjectorScreenChartIcon,
    },
  ];

  const renderContent = () => {
    if (loading) return <LoadingIndicator />;
    if (error || !data?.syndicate?.analytics)
      return <ErrorMessage error={error} refetch={refetch} />;

    return (
      <Outlet
        context={{
          analytics: data.syndicate.analytics as SyndicateAnalyticsData,
          syndicateId,
        }}
      />
    );
  };

  return (
    <SidebarLayout>
      <SidebarLayoutNavigation
        title="Analytics"
        subTitle="Track scheme, company, and yearly performance across your syndicate."
      />
      <SidebarLayoutContent secondaryTabs={tabs}>{renderContent()}</SidebarLayoutContent>
    </SidebarLayout>
  );
};

export function useSyndicateAnalyticsData() {
  return useOutletContext<AnalyticsContextType>();
}

export default SyndicateAnalytics;
